// Flow-reference resolution — swap the design NAMES the flow builder emits for the real Amazon Connect
// resource IDs captured in the import ledger, at export-after-import time.
//
// The §6B flow builder references other resources by NAME (see the knowledge-pack DSL examples:
// `QueueId: 'Sales'`, `EventHooks.CustomerQueue: 'Customer Queue Flow'`, `ContactFlowId: '<flow name>'`).
// After the engineer imports the operational resources to a Connect instance, the import returns the
// real IDs (recorded per-instance in project.connectResources). This resolver rewrites a synthesized
// flow JSON's reference parameters from those names to the imported IDs so the downloaded flow imports
// against the real resources. References it can't resolve (e.g. contact-flow targets — contact flows
// aren't imported by this tool yet) are returned so the UI can warn the engineer.
//
// Pure + framework-free (browser + jest). Flow JSON is plain JSON (Actions[] of { Identifier, Type,
// Parameters, Transitions }) so a JSON deep-clone is safe.

// Reference parameter keys → the ledger collection that defines them. The value is a resource NAME
// (unless it's a JSONPath, e.g. "$.Attributes.x", which is left untouched).
const FLOW_REF_KEYS = {
  QueueId: 'queues',
  HoursOfOperationId: 'hoursOfOperation',
  QuickConnectId: 'quickConnects',
  ContactFlowId: 'flows',
};

// Nested flow references inside an UpdateContactEventHooks `EventHooks` object (all are flow names).
const EVENT_HOOK_KEYS = [
  'CustomerQueue', 'CustomerHold', 'CustomerWhisper', 'AgentWhisper',
  'DefaultCustomerQueue', 'DefaultCustomerHold', 'DefaultCustomerWhisper', 'DefaultAgentWhisper',
];

function lookupId(resourceMap, collection, name) {
  const entry = resourceMap?.[collection]?.[name];
  if (!entry) return null;
  return entry.arn || entry.id || null; // prefer the ARN when present (what flow JSON references)
}

function isResolvableValue(v) {
  return typeof v === 'string' && v.length > 0 && !v.startsWith('$.'); // skip JSONPath / dynamic values
}

/**
 * Resolve a single flow JSON's reference parameters against an instance's import ledger.
 * @param {object} flowJson      Connect flow JSON ({ Actions:[{ Identifier, Type, Parameters }], ... })
 * @param {object} resourceMap   per-collection name→{ id, arn } map (project.connectResources[i].resources)
 * @returns {{ json: object, resolved: number, unresolved: Array<{action,key,name,collection}> }}
 */
export function resolveFlowRefs(flowJson, resourceMap) {
  const json = JSON.parse(JSON.stringify(flowJson || {}));
  const unresolved = [];
  let resolved = 0;

  for (const action of json.Actions || []) {
    const p = action.Parameters;
    if (!p || typeof p !== 'object') continue;

    for (const [key, collection] of Object.entries(FLOW_REF_KEYS)) {
      if (!isResolvableValue(p[key])) continue;
      const id = lookupId(resourceMap, collection, p[key]);
      if (id) { p[key] = id; resolved += 1; }
      else unresolved.push({ action: action.Identifier, key, name: p[key], collection });
    }

    if (p.EventHooks && typeof p.EventHooks === 'object') {
      for (const hook of EVENT_HOOK_KEYS) {
        if (!isResolvableValue(p.EventHooks[hook])) continue;
        const id = lookupId(resourceMap, 'flows', p.EventHooks[hook]);
        if (id) { p.EventHooks[hook] = id; resolved += 1; }
        else unresolved.push({ action: action.Identifier, key: `EventHooks.${hook}`, name: p.EventHooks[hook], collection: 'flows' });
      }
    }
  }

  return { json, resolved, unresolved };
}

export default resolveFlowRefs;
